<template>
    <div class="p-6 bg-[#f9fafb] min-h-screen">
      <!-- Page Header with View Toggle -->
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-2xl font-bold">My Racing Systems</h1>
        
        <!-- View Toggle -->
        <div class="flex items-center bg-white border rounded-lg shadow-sm overflow-hidden" v-if="!loading && !error && systems.length > 0">
          <button 
            @click="viewMode = 'cards'" 
            :class="[`px-4 py-2 flex items-center transition-colors duration-200`, viewMode === 'cards' ? 'bg-gray-800 text-white' : 'text-gray-700 hover:bg-gray-100']">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
            Cards
          </button>
          <button 
            @click="viewMode = 'table'" 
            :class="[`px-4 py-2 flex items-center transition-colors duration-200`, viewMode === 'table' ? 'bg-gray-800 text-white' : 'text-gray-700 hover:bg-gray-100']">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Table
          </button>
        </div>
      </div>
      
      <!-- Systems Display -->
      <div class="space-y-6">
        <!-- Loading indicator -->
        <div v-if="loading" class="flex justify-center items-center py-12">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          <p class="ml-3 text-gray-600">Loading systems...</p>
        </div>
        
        <!-- Error message -->
        <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div class="flex">
            <svg class="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
            <p class="ml-3 text-sm text-red-700">{{ error }}</p>
          </div>
        </div>
        
        <!-- No systems message -->
        <div v-else-if="systems.length === 0" class="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
          <div class="flex">
            <svg class="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            <div class="ml-3">
              <p class="text-sm text-blue-700">You don't have any saved systems yet.</p>
              <p class="text-sm text-blue-700 mt-1">
                <router-link to="/search" class="font-medium underline">Go to the search page</router-link> to create and save a system.
              </p>
            </div>
          </div>
        </div>
        
        <!-- Card View -->
        <div v-else-if="viewMode === 'cards'" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div 
            v-for="(system, index) in systems" 
            :key="index" 
            class="bg-white border rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300 flex flex-col"
          >
            <!-- System Header -->
            <div class="px-6 py-4 border-b flex-grow-0">
              <div class="flex justify-between items-start">
                <h3 class="text-lg font-semibold">{{ system.name }}</h3>
                <p class="text-xs text-gray-500 ml-3">Created: {{ formatDate(system.createdAt) }}</p>
              </div>
              <div class="min-h-[1.25em] mt-0.5">
                <p v-if="system.description" class="text-sm text-gray-600">{{ system.description }}</p>
              </div>
            </div>
            
            <!-- System Metrics - Compact version with comparison -->
            <div v-if="system.metrics" class="grid grid-cols-2 divide-x divide-y">
              <!-- Selections with comparison -->
              <div class="p-4 text-center relative">
                <div class="flex justify-between items-center mb-1">
                  <p class="text-sm text-gray-500">Selections</p>
                  <div class="flex items-center">
                    <span class="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600" :title="`Original value`">
                      {{ system.totalSelections || 0 }}
                    </span>
                  </div>
                </div>
                <p class="text-xl font-bold">
                  {{ system.metrics.totalSelections }}
                  <span v-if="system.metrics.totalSelections !== (system.totalSelections || 0)" 
                    :class="system.metrics.totalSelections > (system.totalSelections || 0) ? 'text-blue-500' : 'text-amber-500'">
                    {{ system.metrics.totalSelections > (system.totalSelections || 0) ? '↑' : '↓' }}
                  </span>
                </p>
              </div>
              
              <!-- Winners with comparison -->
              <div class="p-4 text-center relative">
                <div class="flex justify-between items-center mb-1">
                  <p class="text-sm text-gray-500">Winners</p>
                  <div class="flex items-center">
                    <span class="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600" :title="`Original value`">
                      {{ system.totalWinners || 0 }}
                    </span>
                  </div>
                </div>
                <p class="text-xl font-bold">
                  {{ system.metrics.totalWinners }}
                  <span v-if="system.metrics.totalWinners !== (system.totalWinners || 0)" 
                    :class="system.metrics.totalWinners > (system.totalWinners || 0) ? 'text-green-500' : 'text-red-500'">
                    {{ system.metrics.totalWinners > (system.totalWinners || 0) ? '↑' : '↓' }}
                  </span>
                </p>
              </div>
              
              <!-- Strike Rate with comparison -->
              <div class="p-4 text-center relative">
                <div class="flex justify-between items-center mb-1">
                  <p class="text-sm text-gray-500">Strike Rate</p>
                  <div class="flex items-center">
                    <span class="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600" :title="`Original value`">
                      {{ (system.strikeRate || 0).toFixed(1) }}%
                    </span>
                  </div>
                </div>
                <p class="text-xl font-bold">
                  {{ system.metrics.strikeRate.toFixed(1) }}%
                  <span v-if="system.metrics.strikeRate !== (system.strikeRate || 0)" 
                    :class="system.metrics.strikeRate > (system.strikeRate || 0) ? 'text-green-500' : 'text-red-500'">
                    {{ system.metrics.strikeRate > (system.strikeRate || 0) ? '↑' : '↓' }}
                  </span>
                </p>
              </div>
              
              <!-- Profit/Loss with comparison -->
              <div class="p-4 text-center relative">
                <div class="flex justify-between items-center mb-1">
                  <p class="text-sm text-gray-500">Profit/Loss</p>
                  <div class="flex items-center">
                    <span class="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600" :title="`Original value`">
                      {{ (system.profitLoss || 0) >= 0 ? '+' : '' }}{{ (system.profitLoss || 0).toFixed(1) }}
                    </span>
                  </div>
                </div>
                <p class="text-xl font-bold" :class="system.metrics.totalProfitLoss >= 0 ? 'text-green-500' : 'text-red-500'">
                  {{ system.metrics.totalProfitLoss >= 0 ? '+' : '' }}{{ system.metrics.totalProfitLoss.toFixed(1) }}
                  <span v-if="system.metrics.totalProfitLoss !== (system.profitLoss || 0)" 
                    :class="system.metrics.totalProfitLoss > (system.profitLoss || 0) ? 'text-green-500' : 'text-red-500'">
                    {{ system.metrics.totalProfitLoss > (system.profitLoss || 0) ? '↑' : '↓' }}
                  </span>
                </p>
              </div>
            </div>
            
            <!-- Loading Metrics -->
            <div v-else-if="system.isLoadingMetrics" class="p-6 text-center text-gray-500">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-2"></div>
              <p>Loading metrics...</p>
            </div>
            
            <!-- Metrics Error -->
            <div v-else-if="system.metricsError" class="p-6 text-center text-red-500">
              <p>{{ system.metricsError }}</p>
            </div>
            
            <!-- Actions -->
            <div class="p-4 bg-gray-50 flex justify-between">
              <button 
                class="text-sm text-blue-600 hover:text-blue-800 font-medium"
                @click="viewSystemResults(system)"
              >
                View Results
              </button>
              <button 
                class="text-sm text-red-600 hover:text-red-800 font-medium"
                @click="deleteSystem(system.id)"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
        
        <!-- Table View -->
        <div v-else-if="viewMode === 'table'" class="bg-white border rounded-lg shadow overflow-hidden">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  System Name
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Selections
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Winners
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Strike Rate
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Profit/Loss
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="system in systems" :key="system.id" class="hover:bg-gray-50">
                <td class="px-6 py-4">
                  <div>
                    <div class="text-sm font-medium text-gray-900">{{ system.name }}</div>
                    <div v-if="system.description" class="text-xs text-gray-500 truncate max-w-xs" :title="system.description">{{ system.description }}</div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ formatDate(system.createdAt) }}
                </td>
                <td class="px-6 py-4">
                  <div v-if="system.metrics" class="text-sm font-medium">
                    {{ system.metrics.totalSelections }}
                    <span v-if="system.metrics.totalSelections !== (system.totalSelections || 0)" 
                      :class="system.metrics.totalSelections > (system.totalSelections || 0) ? 'text-blue-500' : 'text-amber-500'">
                      {{ system.metrics.totalSelections > (system.totalSelections || 0) ? '↑' : '↓' }}
                    </span>
                  </div>
                  <div v-else class="text-xs text-gray-500">Loading...</div>
                </td>
                <td class="px-6 py-4">
                  <div v-if="system.metrics" class="text-sm font-medium">
                    {{ system.metrics.totalWinners }}
                    <span v-if="system.metrics.totalWinners !== (system.totalWinners || 0)" 
                      :class="system.metrics.totalWinners > (system.totalWinners || 0) ? 'text-green-500' : 'text-red-500'">
                      {{ system.metrics.totalWinners > (system.totalWinners || 0) ? '↑' : '↓' }}
                    </span>
                  </div>
                  <div v-else class="text-xs text-gray-500">Loading...</div>
                </td>
                <td class="px-6 py-4">
                  <div v-if="system.metrics" class="text-sm font-medium">
                    {{ system.metrics.strikeRate.toFixed(1) }}%
                    <span v-if="system.metrics.strikeRate !== (system.strikeRate || 0)" 
                      :class="system.metrics.strikeRate > (system.strikeRate || 0) ? 'text-green-500' : 'text-red-500'">
                      {{ system.metrics.strikeRate > (system.strikeRate || 0) ? '↑' : '↓' }}
                    </span>
                  </div>
                  <div v-else class="text-xs text-gray-500">Loading...</div>
                </td>
                <td class="px-6 py-4">
                  <div v-if="system.metrics" :class="system.metrics.totalProfitLoss >= 0 ? 'text-green-500' : 'text-red-500'" class="text-sm font-medium">
                    {{ system.metrics.totalProfitLoss >= 0 ? '+' : '' }}{{ system.metrics.totalProfitLoss.toFixed(1) }}
                    <span v-if="system.metrics.totalProfitLoss !== (system.profitLoss || 0)" 
                      :class="system.metrics.totalProfitLoss > (system.profitLoss || 0) ? 'text-green-500' : 'text-red-500'">
                      {{ system.metrics.totalProfitLoss > (system.profitLoss || 0) ? '↑' : '↓' }}
                    </span>
                  </div>
                  <div v-else class="text-xs text-gray-500">Loading...</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <button 
                    class="text-sm text-blue-600 hover:text-blue-800 font-medium mr-4"
                    @click="viewSystemResults(system)"
                  >
                    View
                  </button>
                  <button 
                    class="text-sm text-red-600 hover:text-red-800 font-medium"
                    @click="deleteSystem(system.id)"
                  >
                    Delete
                  </button>
                </td>
              </tr>
              
              <!-- Empty state for metrics loading/error in table view -->
              <tr v-if="systems.length === 0">
                <td colspan="7" class="px-6 py-4 text-center text-gray-500">No systems found</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { ref, onMounted } from 'vue';
  import { useRouter } from 'vue-router';
  import { useStore } from 'vuex';
  import apiService from '@/services/api.service';
  
  export default {
    name: 'SystemsView',
    setup() {
      const router = useRouter();
      const systems = ref([]);
      const loading = ref(true);
      const error = ref(null);
      const viewMode = ref('cards'); // Default view mode is cards
      const store = useStore();
      
      // Get userId from the store
      const userId = store.state.user?.id;
      
      // Fetch all saved systems
      const fetchSystems = async () => {
        try {
          loading.value = true;
          error.value = null;

          if (!userId) {
            console.error('User not authenticated or user ID not found.');
            error.value = 'Authentication required to view systems.';
            loading.value = false;
            // Optionally redirect to login
            router.push('/login');
            return;
          }

          const response = await apiService.getSystems(userId);
          systems.value = response.data.map(system => ({
            ...system,
            // Use metrics already included in the API response
            metrics: {
              totalSelections: system.recentSelections || 0,
              totalWinners: system.recentWinners || 0,
              strikeRate: system.recentStrikeRate || 0,
              totalProfitLoss: system.recentProfitLoss || 0
            },
            isLoadingMetrics: false,
            metricsError: null
          }));
          
          // Fetch metrics for each system
          //await Promise.all(systems.value.map(fetchSystemMetrics));
        } catch (err) {
          console.error('Error fetching systems:', err);
          error.value = 'Failed to load systems. Please try again.';
        } finally {
          loading.value = false;
        }
      };
      
      // // Fetch metrics for a specific system
      // const fetchSystemMetrics = async (system) => {
      //   try {
      //     const response = await apiService.getSystemMetrics(system.id);
      //     system.metrics = response.data;
      //     system.isLoadingMetrics = false;
      //   } catch (err) {
      //     console.error(`Error fetching metrics for system ${system.id}:`, err);
      //     system.metricsError = 'Failed to load metrics';
      //     system.isLoadingMetrics = false;
      //   }
      // };
      
      // Delete a system
      const deleteSystem = async (systemId) => {
        if (!confirm('Are you sure you want to delete this system?')) {
          return;
        }
        
        try {
          // Add delete endpoint to API service if not already present
          // await apiService.deleteSystem(systemId);
          // For now, just remove from local array
          systems.value = systems.value.filter(s => s.id !== systemId);
          alert('System deleted successfully');
        } catch (err) {
          console.error('Error deleting system:', err);
          alert('Failed to delete system. Please try again.');
        }
      };
      
      // View system results
      const viewSystemResults = (system) => {
        // Parse the filters JSON if not already an object
        let filters = system.filters;
        
        // If filters is a string, parse it
        if (typeof filters === 'string') {
          filters = JSON.parse(filters);
        } else {
          // Make a copy to avoid mutating the original
          filters = JSON.stringify(filters);
          filters = JSON.parse(filters);
        }
        
        // Add the system's createdAt date to use as the start date for filtering
        filters.createdAt = system.createdAt;
        
        // Use localStorage to pass the filter data between pages
        localStorage.setItem('activeSystemFilters', JSON.stringify(filters));
        
        // Navigate to search page with the filters
        router.push({
          path: '/search',
          query: { systemId: system.id }
        });
      };
      
      // Format date for display
      const formatDate = (dateString) => {
        if (!dateString) return '';
        
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { 
          day: 'numeric', 
          month: 'short', 
          year: 'numeric' 
        });
      };
      
      // Load systems when component mounts
      onMounted(() => {
        fetchSystems();
      });
      
      return {
        systems,
        loading,
        error,
        viewMode,
        formatDate,
        deleteSystem,
        viewSystemResults
      };
    }
  };
  </script>
  
  <style scoped>
  /* Add any component-specific styles here */
  </style>